import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { api, TenantDetail, Plan, ApiError } from '@/lib/api';

export default function TenantEditPage() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug as string;

  const [tenant, setTenant] = React.useState<TenantDetail | null>(null);
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingPlans, setIsLoadingPlans] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    platformName: '',
    status: 'active' as 'active' | 'inactive' | 'suspended',
    planId: '',
  });

  React.useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setIsLoadingPlans(true);
        setError(null);

        const [tenantData, plansData] = await Promise.all([
          api.getTenant(slug),
          api.getPlans(),
        ]);

        setTenant(tenantData);
        setPlans(plansData);
        setFormData({
          platformName: tenantData.platformName,
          status: tenantData.status as 'active' | 'inactive' | 'suspended',
          planId: tenantData.planId,
        });
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Erro ao carregar plataforma');
      } finally {
        setIsLoading(false);
        setIsLoadingPlans(false);
      }
    };

    loadData();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      await api.updateTenant(slug, formData);
      navigate(`/tenants/${slug}`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao atualizar plataforma');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
          <p className="text-gray-500">Carregando plataforma...</p>
        </div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Plataforma nao encontrada</h1>
        </div>
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-semibold text-red-800">Erro</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/tenants/${slug}`}
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Editar Plataforma</h1>
          <p className="text-gray-500 mt-1">
            Atualize as informacoes da plataforma {tenant?.platformName}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Informacoes Basicas</h2>
            <p className="text-sm text-gray-500 mt-1">Dados principais da plataforma</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome da Plataforma *</label>
                <input
                  type="text"
                  required
                  value={formData.platformName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, platformName: e.target.value }))}
                  placeholder="Ex: Portal Suvinil"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as any }))}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                  <option value="suspended">Suspenso</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Subdominio</label>
              <div className="flex">
                <input
                  type="text"
                  disabled
                  value={slug}
                  className="flex-1 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 cursor-not-allowed"
                />
                <span className="flex items-center rounded-r-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500 font-medium">
                  .nozcomunidades.com.br
                </span>
              </div>
              <p className="text-xs text-gray-400">
                O subdominio nao pode ser alterado apos a criacao
              </p>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Plano</h2>
            <p className="text-sm text-gray-500 mt-1">Selecione o plano de assinatura</p>
          </div>
          <div className="p-6">
            {isLoadingPlans ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
              </div>
            ) : (
              <div className="space-y-3">
                {plans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all hover:border-gray-300 ${
                      formData.planId === plan.id
                        ? 'border-[#0057FF] bg-[#0057FF]/5'
                        : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="planId"
                      value={plan.id}
                      checked={formData.planId === plan.id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, planId: e.target.value }))}
                      className="h-5 w-5 text-[#0057FF]"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                        {plan.price === 0 ? (
                          <span className="text-sm text-gray-500">Sob Consulta</span>
                        ) : (
                          <span className="text-sm font-medium text-[#0057FF]">
                            {new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            }).format(plan.price)}
                            /{plan.billingCycle === 'monthly' ? 'mês' : 'ano'}
                          </span>
                        )}
                      </div>
                      {plan.description && (
                        <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to={`/tenants/${slug}`}
            className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Alteracoes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
